import { ComponentProps, FC, ReactNode } from "react";
import { Row, Col, Grid, Space, Typography, Tag } from "antd";

import {
  FileDoneOutlined,
  CompassOutlined,
  ExclamationCircleOutlined,
  RightOutlined,
  CalendarOutlined
} from "@ant-design/icons";

import { classname } from "@ctra/utils";

import { Barn, Icon } from "../../../elements";
import MobileViewRow from "../MobileViewRow";
import styles from "./GamePlanTaskItem.module.less";

export interface GamePlanTaskItemProps extends ComponentProps<typeof Row> {
  /**
   * task label
   */
  label: string;
  /**
   * related game plan
   */
  gamePlan: string;
  /**
   * farm name
   */
  farm?: string;
  /**
   * task due date
   */
  dueDate?: ReactNode;
  /**
   * overdue indicator
   */
  overdue?: string;
}

const { Text } = Typography;

/**
 * Mobile view for the game plan task entry
 * @param {string} label
 * @param {string} gamePlan
 * @param {string | undefined} farm
 * @param {React.ReactElement<any, string | React.JSXElementConstructor<any>> | string | number | {} | Iterable<React.ReactNode> | React.ReactPortal | boolean | null | undefined} dueDate
 * @param {string | undefined} overdue
 * @param {string | undefined} className
 * @param {Pick<GamePlanTaskItemProps & {children?: React.ReactNode | undefined}, "gutter" | "align" | "justify" | "prefixCls" | "wrap" | "defaultChecked" | "defaultValue" | "suppressContentEditableWarning" | "suppressHydrationWarning" | "accessKey" | "contentEditable" | "contextMenu" | "dir" | "draggable" | "hidden" | "id" | "lang" | "placeholder" | "slot" | "spellCheck" | "style" | "tabIndex" | "title" | "translate" | "radioGroup" | "role" | "about" | "datatype" | "inlist" | "prefix" | "property" | "resource" | "typeof" | "vocab" | "autoCapitalize" | "autoCorrect" | "autoSave" | "color" | "itemProp" | "itemScope" | "itemType" | "itemID" | "itemRef" | "results" | "security" | "unselectable" | "inputMode" | "is" | "aria-activedescendant" | "aria-atomic" | "aria-autocomplete" | "aria-busy" | "aria-checked" | "aria-colcount" | "aria-colindex" | "aria-colspan" | "aria-controls" | "aria-current" | "aria-describedby" | "aria-details" | "aria-disabled" | "aria-dropeffect" | "aria-errormessage" | "aria-expanded" | "aria-flowto" | "aria-grabbed" | "aria-haspopup" | "aria-hidden" | "aria-invalid" | "aria-keyshortcuts" | "aria-label" | "aria-labelledby" | "aria-level" | "aria-live" | "aria-modal" | "aria-multiline" | "aria-multiselectable" | "aria-orientation" | "aria-owns" | "aria-placeholder" | "aria-posinset" | "aria-pressed" | "aria-readonly" | "aria-relevant" | "aria-required" | "aria-roledescription" | "aria-rowcount" | "aria-rowindex" | "aria-rowspan" | "aria-selected" | "aria-setsize" | "aria-sort" | "aria-valuemax" | "aria-valuemin" | "aria-valuenow" | "aria-valuetext" | "children" | "dangerouslySetInnerHTML" | "onCopy" | "onCopyCapture" | "onCut" | "onCutCapture" | "onPaste" | "onPasteCapture" | "onCompositionEnd" | "onCompositionEndCapture" | "onCompositionStart" | "onCompositionStartCapture" | "onCompositionUpdate" | "onCompositionUpdateCapture" | "onFocus" | "onFocusCapture" | "onBlur" | "onBlurCapture" | "onChange" | "onChangeCapture" | "onBeforeInput" | "onBeforeInputCapture" | "onInput" | "onInputCapture" | "onReset" | "onResetCapture" | "onSubmit" | "onSubmitCapture" | "onInvalid" | "onInvalidCapture" | "onLoad" | "onLoadCapture" | "onError" | "onErrorCapture" | "onKeyDown" | "onKeyDownCapture" | "onKeyPress" | "onKeyPressCapture" | "onKeyUp" | "onKeyUpCapture" | "onAbort" | "onAbortCapture" | "onCanPlay" | "onCanPlayCapture" | "onCanPlayThrough" | "onCanPlayThroughCapture" | "onDurationChange" | "onDurationChangeCapture" | "onEmptied" | "onEmptiedCapture" | "onEncrypted" | "onEncryptedCapture" | "onEnded" | "onEndedCapture" | "onLoadedData" | "onLoadedDataCapture" | "onLoadedMetadata" | "onLoadedMetadataCapture" | "onLoadStart" | "onLoadStartCapture" | "onPause" | "onPauseCapture" | "onPlay" | "onPlayCapture" | "onPlaying" | "onPlayingCapture" | "onProgress" | "onProgressCapture" | "onRateChange" | "onRateChangeCapture" | "onSeeked" | "onSeekedCapture" | "onSeeking" | "onSeekingCapture" | "onStalled" | "onStalledCapture" | "onSuspend" | "onSuspendCapture" | "onTimeUpdate" | "onTimeUpdateCapture" | "onVolumeChange" | "onVolumeChangeCapture" | "onWaiting" | "onWaitingCapture" | "onAuxClick" | "onAuxClickCapture" | "onClick" | "onClickCapture" | "onContextMenu" | "onContextMenuCapture" | "onDoubleClick" | "onDoubleClickCapture" | "onDrag" | "onDragCapture" | "onDragEnd" | "onDragEndCapture" | "onDragEnter" | "onDragEnterCapture" | "onDragExit" | "onDragExitCapture" | "onDragLeave" | "onDragLeaveCapture" | "onDragOver" | "onDragOverCapture" | "onDragStart" | "onDragStartCapture" | "onDrop" | "onDropCapture" | "onMouseDown" | "onMouseDownCapture" | "onMouseEnter" | "onMouseLeave" | "onMouseMove" | "onMouseMoveCapture" | "onMouseOut" | "onMouseOutCapture" | "onMouseOver" | "onMouseOverCapture" | "onMouseUp" | "onMouseUpCapture" | "onSelect" | "onSelectCapture" | "onTouchCancel" | "onTouchCancelCapture" | "onTouchEnd" | "onTouchEndCapture" | "onTouchMove" | "onTouchMoveCapture" | "onTouchStart" | "onTouchStartCapture" | "onPointerDown" | "onPointerDownCapture" | "onPointerMove" | "onPointerMoveCapture" | "onPointerUp" | "onPointerUpCapture" | "onPointerCancel" | "onPointerCancelCapture" | "onPointerEnter" | "onPointerEnterCapture" | "onPointerLeave" | "onPointerLeaveCapture" | "onPointerOver" | "onPointerOverCapture" | "onPointerOut" | "onPointerOutCapture" | "onGotPointerCapture" | "onGotPointerCaptureCapture" | "onLostPointerCapture" | "onLostPointerCaptureCapture" | "onScroll" | "onScrollCapture" | "onWheel" | "onWheelCapture" | "onAnimationStart" | "onAnimationStartCapture" | "onAnimationEnd" | "onAnimationEndCapture" | "onAnimationIteration" | "onAnimationIterationCapture" | "onTransitionEnd" | "onTransitionEndCapture" | "ref" | "key">} rest
 * @return {JSX.Element}
 * @constructor
 */
const MobileView: FC<GamePlanTaskItemProps> = ({
  label,
  gamePlan,
  farm,
  dueDate,
  overdue,
  className,
  ...rest
}) => {
  return (
    <Row
      className={classname("ctra-farmOverview-gamePlanTaskEntry--mobile", styles.GamePlanTaskItem, className)}
      align="middle"
      gutter={[16, 16]}
      wrap={false}
      {...rest}
    >
      <Col>
        <Row gutter={[16, 16]}>
          <MobileViewRow icon={<FileDoneOutlined />} className={styles.Label}>
            <Text>{label}</Text>
          </MobileViewRow>
          {overdue && (
            <MobileViewRow icon={<ExclamationCircleOutlined />} className={styles.Overdue}>
              <Text>{overdue}</Text>
            </MobileViewRow>
          )}
          <MobileViewRow icon={<CompassOutlined />} className={styles.GamePlan}>
            <Text>{gamePlan}</Text>
          </MobileViewRow>
          {farm && (
            <MobileViewRow icon={<Icon component={Barn} />}>
              <Tag color="purple">{farm}</Tag>
            </MobileViewRow>
          )}
          {dueDate && <MobileViewRow icon={<CalendarOutlined />}>{dueDate}</MobileViewRow>}
        </Row>
      </Col>
      <Col>
        <Text type="secondary" className={styles.Caret}>
          <RightOutlined />
        </Text>
      </Col>
    </Row>
  );
};

/**
 * Farm overview game plan task entry component
 * @param {React.PropsWithChildren<GamePlanTaskItemProps>} props
 * @return {JSX.Element | null}
 * @constructor
 */
const GamePlanTaskItem: FC<GamePlanTaskItemProps> = (props) => {
  const { label, gamePlan, farm, dueDate = null, overdue, className, ...rest } = props;
  const { md } = Grid.useBreakpoint();

  return md ? (
    <Row
      wrap={false}
      align="middle"
      gutter={[16, 0]}
      className={classname("ctra-farmOverview-gamePlanTaskEntry", styles.GamePlanTaskItem, className)}
      {...rest}
    >
      <Col className={styles.Icon}>
        <FileDoneOutlined />
      </Col>
      <Col flex={1} className={styles.Label}>
        <Text>{label}</Text>
      </Col>
      <Col className={styles.Overdue}>
        {overdue && (
          <Space>
            <ExclamationCircleOutlined />
            <Text>{overdue}</Text>
          </Space>
        )}
      </Col>
      <Col className={styles.GamePlan}>
        <Space>
          <CompassOutlined />
          <Text>{gamePlan}</Text>
        </Space>
      </Col>
      <Col>
        {farm && (
          <Tag color="purple" icon={<Icon component={Barn} />}>
            {farm}
          </Tag>
        )}
      </Col>
      <Col>{dueDate}</Col>
      <Col>
        <Text type="secondary" className={styles.Caret}>
          <RightOutlined />
        </Text>
      </Col>
    </Row>
  ) : (
    <MobileView {...props} />
  );
};

export default GamePlanTaskItem;
